import {Button} from '@primer/react-brand/esm'
import {useMemo} from 'react'

import {
  renderContentfulDownloadAction,
  renderContentfulDownloadActionFeedback,
  useResolvedDownloadOptions,
  type RenderContentfulDownloadActionMenuOptions,
} from '@github-ui/swp-core/components/contentful/ContentfulDownloadActionMenu'
import type {OSAwareDownloadActionMenuAnalytics} from '@github-ui/swp-core/components/OSAwareDownloadActionMenu'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {ContentfulDownloadOptionsSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/downloadOptions'
import {isLink, type Link} from '@github-ui/swp-core/schemas/contentful/contentTypes/link'

import type {GenericCtaActionInput} from '../../lib/types/contentful'

type Options = Pick<RenderContentfulDownloadActionMenuOptions, 'size'> & {
  analytics: OSAwareDownloadActionMenuAnalytics
  className?: string
  variant?: Link['fields']['variant']
}

export function useGenericCtaAction(
  cta: GenericCtaActionInput | undefined,
  {analytics, className, size, variant}: Options,
) {
  const downloadOptions = useMemo(() => {
    const parsed = ContentfulDownloadOptionsSchema.safeParse(cta)
    return parsed.success ? parsed.data : undefined
  }, [cta])
  const resolved = useResolvedDownloadOptions(downloadOptions)
  const feedback = renderContentfulDownloadActionFeedback(resolved)

  if (downloadOptions) {
    const action = renderContentfulDownloadAction({
      component: downloadOptions,
      resolved,
      analytics,
      size,
      variant: variant === 'accent' ? 'secondary' : variant,
    })

    return {
      action,
      feedback,
      isDownloadAction: true,
    }
  }

  return {
    action: isLink(cta) ? (
      <Button
        as="a"
        className={className}
        href={cta.fields.href}
        size={size}
        variant={variant}
        {...getAnalyticsEvent({
          action: cta.fields.text,
          tag: analytics.tag ?? 'button',
          context: analytics.context,
          location: analytics.location,
        })}
      >
        {cta.fields.text}
      </Button>
    ) : null,
    feedback,
    isDownloadAction: false,
  }
}
